import React, { createContext, useMemo, useReducer } from 'react';
import PropTypes from 'prop-types';

import { INTEGRATIONS } from 'screens/Integrations/helpers/constants';
import useProductsMatch from 'screens/Integrations/hooks/useProductsMatch';
import reducer, { SET_MATCHES, SET_FETCHED_MATCHES } from '../reducer';

export const initialState = {
  matches: [],
  fetchedMatches: [],
  errorMessage: '',
  matchEntityType: ''
};

export const ProductsMatchContext = createContext(initialState);

const ProductsMatchProvider = ({
  integrationType = INTEGRATIONS.simplot,
  children = null,
  onCompleteMatch = null
}) => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    integrationType,
    children,
    matchEntityType: ['Product']
  });

  const {
    getProductMatches,
    createProductMatches,
    loading: isLoading
  } = useProductsMatch();

  const updateMatches = (payload, isFetched = false) => {
    dispatch({
      type: !isFetched ? SET_MATCHES : SET_FETCHED_MATCHES,
      payload
    });
  };

  // eslint-disable-next-line consistent-return
  const submitMatches = async () => {
    const newOrUpdatedMatches = state.matches.filter(
      m =>
        state.fetchedMatches.findIndex(
          fetchedMatch =>
            m.cwfId === fetchedMatch.cwfId &&
            m.vendorId === fetchedMatch.vendorId
        ) === -1
    );

    if (newOrUpdatedMatches.length === 0) {
      return onCompleteMatch();
    }

    const response = await createProductMatches({
      vendor: INTEGRATIONS.simplot,
      matches: newOrUpdatedMatches
    });

    if (response?.status === 201) {
      onCompleteMatch();
    }

    return response;
  };

  const fetchMatches = async () => {
    const response = await getProductMatches({
      vendor: INTEGRATIONS.simplot,
      matchTypes: state.matchEntityType
    });

    if (response?.status === 200) {
      updateMatches(response.data, true);
    }
  };

  const memoized = useMemo(
    () => ({
      state,
      integrationContext: INTEGRATIONS.simplot,
      dispatch,
      updateMatches,
      submitMatches,
      fetchMatches,
      isLoading
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, isLoading]
  );

  return (
    <ProductsMatchContext.Provider value={memoized}>
      {children}
    </ProductsMatchContext.Provider>
  );
};

ProductsMatchProvider.propTypes = {
  children: PropTypes.node,
  integrationType: PropTypes.string,
  onCompleteMatch: PropTypes.func
};

export default ProductsMatchProvider;
